import React, {useState, useEffect, useRef} from 'react';
import axios from 'axios';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { API_BASE_URL } from "../config";
import { Draggable } from '@fullcalendar/interaction';
import frLocale from '@fullcalendar/core/locales/fr';
import '../assets/css/Calendar.css';

function CalendarComponent() {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [events, setEvents] = useState([]);
    const [tasksToSchedule, setTasksToSchedule] = useState([])
    const [isDragging, setIsDragging] = useState(false);
    const mousePosition = useRef({ x: 0, y: 0 });
    const dragTimeoutRef = useRef(null);
    const isInTrashZone = useRef(false);

    useEffect(() => {
        return () => {
            if (dragTimeoutRef.current) {
                clearTimeout(dragTimeoutRef.current);
            }
        };
    }, []);

    // Fetch users once on component mount
    useEffect(() => {
        document.title = "Calendrier";

        axios.post(`${API_BASE_URL}/get_users`)
            .then(res => {
                console.log(res.data)
                // Assuming res.data is an array of user objects
                setUsers(res.data);
                // Set a default user based on localStorage or the first user returned
                const defaultUserId = localStorage.user_id || res.data[0].id;
                setSelectedUser(defaultUserId);
            })
            .catch(err => {
                console.error('Error fetching users:', err);
            });
    }, []);

    useEffect(() => {
        const handleMouseMove = (e) => {
            mousePosition.current = { x: e.clientX, y: e.clientY };

            const trashEl = document.getElementById('trash');
            if (!trashEl) return;

            const rect = trashEl.getBoundingClientRect();
            const inTrash =
                e.clientX >= rect.left &&
                e.clientX <= rect.right &&
                e.clientY >= rect.top &&
                e.clientY <= rect.bottom;

            isInTrashZone.current = inTrash; // ✅ on le garde en mémoire

            trashEl.classList.toggle('hovered', inTrash);
        };

        document.addEventListener('mousemove', handleMouseMove);
        return () => document.removeEventListener('mousemove', handleMouseMove);
    }, []);

    useEffect(() => {
        if (selectedUser) {
            axios.post(`${API_BASE_URL}/get_tasks_to_schedule`, { user_id: selectedUser })
                .then((res) => {
                    console.log(res.data.data)
                    setTasksToSchedule(res.data.data); // ✅ maintenant ça met à jour la liste
                })
                .catch(err => {
                    console.error("Erreur récupération tâches à planifier :", err);
                });
        }
    }, [selectedUser]);

    useEffect(() => {
        const draggableEl = document.getElementById('external-tasks');
        if (draggableEl) {
            new Draggable(draggableEl, {
                itemSelector: '.fc-task',
                eventData: function(eventEl) {
                    return {
                        title: eventEl.getAttribute('data-title'),
                        id: eventEl.getAttribute('data-id'),
                    };
                }
            });
        }
    }, []);

    useEffect(() => {
        if (selectedUser) {
            axios.post(`${API_BASE_URL}/get_user_tasks`, {
                id: selectedUser
            })
                .then(res => {
                    console.log(res)
                    const tasks = res.data.data.map(task => ({
                        id: task.id,
                        title: `${task.title}`, // ou laisser vide si tu veux custom rendering
                        start: new Date(task.start_date),
                        end: new Date(task.finish_date),
                        userId: task.user_id,
                        name_1: task.name_1,
                        intern_name: task.intern_name,
                        url: `/positions/${task.position_id}` // 🔗 à adapter selon ton routing
                    }));
                    console.log(res.data.data)
                    console.log(tasks)
                    setEvents(tasks);
                })
                .catch(err => {
                    console.error('Error fetching tasks:', err);
                });
        }
    }, [selectedUser, tasksToSchedule]); // ✅ robustifier le sync


    // Update calendar when the user selects another from the dropdown
    const handleUserChange = (e) => {
        const userId = e.target.value;
        setSelectedUser(userId);
    };

    // Handle task drag and drop to update task time slots
    const handleEventDrop = (info) => {
        const updatedTask = {
            id: info.event.id,
            start_date: formatDate(info.event.start),   // ✅
            finish_date: formatDate(info.event.end),    // ✅
            user_id: selectedUser
        };
        axios.post(`${API_BASE_URL}/update_task_calendar`, updatedTask)
            .then(res => {
                console.log('Task updated:', res.data);
            })
            .catch(err => {
                console.error('Error updating task:', err);
            });
    };

    const formatDate = (date) => {
        // Si c'est une string, on la convertit
        if (!(date instanceof Date)) {
            date = new Date(date);
        }

        // Vérifie si c'est un Date valide
        if (isNaN(date.getTime())) {
            console.warn("⚠️ formatDate() - date invalide :", date);
            return null;
        }

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');

        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };
    const handleEventResize = (info) => {
        const updatedTask = {
            id: info.event.id,
            start_date: formatDate(info.event.start),   // 🟢 locale, sans Z
            finish_date: formatDate(info.event.end),
            user_id: selectedUser
        };

        axios.post(`${API_BASE_URL}/update_task_calendar`, updatedTask)
            .then(res => {
                console.log('Task resized:', res.data);
            })
            .catch(err => {
                console.error('Error resizing task:', err);
            });
    };

    return (
        <div>
            {/* User Selection Dropdown */}
            <select onChange={handleUserChange} value={selectedUser}>
                {users.map(user => (
                    <option key={user.id} value={user.id}>
                        {user.username}
                    </option>
                ))}
            </select>

            <div className={"display-flex"}>

                <div className="task-list" style={{width: '20%', marginRight: '1rem'}}>
                    <h3>Tâches</h3>
                    <div id="external-tasks">
                        {tasksToSchedule.map(task => (
                            <div
                                data-name_1={task.name_1}
                                data-intern_name={task.intern_name}
                                data-position_id={task.position_id}
                                key={task.id}
                                className="fc-task"
                                data-id={task.task_id}
                                data-title={task.title}
                                style={{
                                    margin: '4px 0',
                                    padding: '6px 8px',
                                    backgroundColor: '#e0e0e0',
                                    borderRadius: '4px',
                                    cursor: 'grab'
                                }}
                            >
                                <span>{task.name_1}</span>
                                <span>{task.intern_name}</span>
                            </div>
                        ))}

                    </div>
                </div>

                {/* Calendar Component */}
                <div style={{flex: 1}}>
                    <FullCalendar
                        locale={frLocale}
                        timeZone={'Europe/Zurich'}
                        slotLabelFormat={{
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false,
                        }}
                        plugins={[timeGridPlugin, interactionPlugin]}
                        initialView="timeGridWeek"
                        events={events}
                        editable={true}
                        slotDuration="00:15:00"

                        hiddenDays={[0, 6]} // 0 = dimanche, 6 = samedi
                        slotMinTime="08:00:00"
                        slotMaxTime="18:00:00"
                        businessHours={[
                            {
                                daysOfWeek: [1, 2, 3, 4, 5],
                                startTime: '08:00',
                                endTime: '12:00'
                            },
                            {
                                daysOfWeek: [1, 2, 3, 4, 5],
                                startTime: '13:00',
                                endTime: '18:00'
                            }
                        ]}
                        droppable={true}
                        eventContent={(arg) => {
                            const {name_1, intern_name} = arg.event.extendedProps;
                            const title = arg.event.title;

                            return (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        fontSize: '0.75rem',
                                        whiteSpace: 'normal',
                                        overflowWrap: 'break-word'
                                    }}
                                    title={title} // ✅ tooltip natif (le titre apparaît au hover)
                                >
                                    <strong>{name_1}</strong>
                                    <span>{intern_name}</span>
                                    <em style={{color: '#444'}}>{title}</em>
                                </div>
                            );
                        }}


                        eventDrop={handleEventDrop}
                        eventResize={handleEventResize}
                        eventDragStart={() => setIsDragging(true)}
                        eventDragStop={(info) => {
                            const trashEl = document.getElementById('trash');
                            const { x, y } = mousePosition.current;

                            let inTrashZone = false;

                            if (trashEl) {
                                const rect = trashEl.getBoundingClientRect();
                                inTrashZone =
                                    x >= rect.left &&
                                    x <= rect.right &&
                                    y >= rect.top &&
                                    y <= rect.bottom;

                                trashEl.classList.remove('hovered');
                            }

                            if (inTrashZone) {
                                axios.post(`${API_BASE_URL}/update_task_calendar`, {
                                    id: info.event.id,
                                    start_date: null,
                                    finish_date: null,
                                    user_id: selectedUser
                                }).then(() => {
                                    setEvents(prev => prev.filter(e => e.id !== info.event.id));
                                    axios.post(`${API_BASE_URL}/get_tasks_to_schedule`, {
                                        user_id: selectedUser
                                    }).then((res) => {
                                        setTasksToSchedule(res.data.data);
                                    });
                                }).catch(err => {
                                    console.error("Erreur suppression calendrier :", err);
                                });
                            }

                            // ✨ Délai pour garder la poubelle visible un peu après le drop
                            setTimeout(() => {
                                setIsDragging(false);
                            }, 300);
                        }}

                        drop={(info) => {

                            console.log(info)
                            const startDate = new Date(info.date);
                            const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // +1 hour

                            const newEvent = {
                                id: info.draggedEl.getAttribute('data-id'),
                                title: info.draggedEl.getAttribute('data-title'),
                                start: startDate,
                                end: endDate,
                                userId: selectedUser,
                                name_1: info.draggedEl.getAttribute('data-name_1'),
                                intern_name: info.draggedEl.getAttribute('data-intern_name'),
                                url: `/positions/${info.draggedEl.getAttribute('data-position_id')}`
                            };

                            console.log(newEvent)

                            axios.post(`${API_BASE_URL}/update_task_calendar`, {
                                id: newEvent.id,
                                start_date: formatDate(newEvent.start),
                                finish_date: formatDate(newEvent.end),
                                user_id: newEvent.userId
                            })

                                .then(res => {
                                    console.log('ok?')
                                    console.log('Task placed on calendar:', res.data);

                                    // Ajouter au calendrier
                                    setEvents(prev => [...prev, newEvent]);

                                }).catch(err => {
                                console.error('Error placing task:', err);
                            });
                        }}
                    />

                    {localStorage.user_role === "Administrateur" &&(
                    isDragging && (
                        <div id="trash" className="trash-zone">
                            🗑️ Supprimer
                        </div>
                    ))
                    }

                </div>
            </div>
        </div>
    );
}

export default CalendarComponent;
